import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { Button, type ButtonProps } from '@/components/ui/button';

/**
 * NB: the group is `flex-col` on purpose — that is what makes the `block-end`
 * addon render as a full-width footer row under the input. Children therefore
 * STACK. Anything meant to sit on one line (an inline addon next to its input)
 * needs its own `flex items-center` wrapper at the call site, or it will drop
 * onto a second row and overflow a fixed-height group. See ServiceConfigRow and
 * PackageMultiOptionRates for the pattern.
 */
const inputGroupVariants = cva(
  'flex flex-col rounded-lg border border-stone-200 bg-white shadow-sm transition-all duration-200 focus-within:ring-2 focus-within:ring-ring/20 focus-within:ring-offset-0 focus-within:border-primary hover:border-primary/50',
  {
    variants: {
      size: {
        default: '',
        sm: '',
        lg: '',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

interface InputGroupProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof inputGroupVariants> {}

const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(
  ({ className, size, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(inputGroupVariants({ size }), className)}
        {...props}
      />
    );
  }
);
InputGroup.displayName = 'InputGroup';

const inputGroupAddonVariants = cva(
  'flex items-center text-muted-foreground shrink-0',
  {
    variants: {
      align: {
        'inline-start': 'pl-3 justify-start',
        'inline-end': 'pr-3 justify-end',
        'block-end': 'px-3 py-2 justify-between border-t border-stone-100',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  }
);

interface InputGroupAddonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof inputGroupAddonVariants> {}

const InputGroupAddon = React.forwardRef<HTMLDivElement, InputGroupAddonProps>(
  ({ className, align, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(inputGroupAddonVariants({ align }), className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
InputGroupAddon.displayName = 'InputGroupAddon';

const InputGroupInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>(({ className, type, ...props }, ref) => {
  return (
    <input
      type={type}
      className={cn(
        'flex-1 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      ref={ref}
      {...props}
    />
  );
});
InputGroupInput.displayName = 'InputGroupInput';

const InputGroupTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<'textarea'>
>(({ className, ...props }, ref) => {
  return (
    <textarea
      className={cn(
        'flex-1 resize-none bg-transparent px-3 py-3 text-sm text-stone-900 placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      ref={ref}
      {...props}
    />
  );
});
InputGroupTextarea.displayName = 'InputGroupTextarea';

export type InputGroupButtonProps = ButtonProps;

const InputGroupButton = React.forwardRef<HTMLButtonElement, InputGroupButtonProps>(
  ({ className, variant = 'ghost', size = 'sm', ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(className)}
        {...props}
      />
    );
  }
);
InputGroupButton.displayName = 'InputGroupButton';

export {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupTextarea,
  InputGroupButton,
};
